import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, cwave, envelope, inOut, inQuad, keys, lerp, outBack, outCubic, outExpo, outQuad, phase, settle, smooth, span, wave, type Ease } from '../curves.ts';
import { timeline, type Ch, type KeyPose } from '../pose.ts';

/*
 * Cuervo de hollín (models/crow.ts): a soot crow of the flying swarm. It hovers at its rest height and every
 * clip keeps it in the air except `die`, which drops it to the ground in a heap.
 *
 * Wings are authored for the right one (+X) and mirrored: on `arm` +Z lifts it, +Y sweeps it back, +X turns
 * the leading edge down; on `hand` (the wrist) the same, so +Y folds the primaries back along the arm.
 * +X pitches the body and the head nose down, opens the jaw, lifts the tail and swings the talons back.
 *
 * The beat is a real bird's: a fast, driving downstroke with the hand spread and a little forward, then an
 * upstroke with the wrist leading and the hand folded back, flicking open again at the top.
 */

type B = 'root' | 'body' | 'head' | 'jaw' | 'armL' | 'handL' | 'armR' | 'handR' | 'tail' | 'legs';
type P = KeyPose<B>;

const sin = Math.sin;
/** The ground under the model's hovering origin (models/crow.ts `CROW_HOVER`), less where the body rests on it. */
const GROUND = -0.92;
/** The strike's ease: it accelerates into the blow but spreads over the 30 Hz samples. */
const snap: Ease = (k) => k ** 1.5;

const mirror = (c: Ch): Ch => [c[0], -c[1], -c[2], -(c[3] ?? 0), c[4] ?? 0, c[5] ?? 0, c[6] ?? 1];
/** Both wings from the right one's arm and hand. */
const wings = (arm: Ch, hand: Ch = [0, 0, 0]): P => ({ armR: arm, handR: hand, armL: mirror(arm), handL: mirror(hand) });

/** A wing's pose for the right side: arm and hand rotations. */
interface W { ax: number; ay: number; az: number; hx: number; hy: number; hz: number }

/** One wingbeat at `p` (0 = top of the stroke, wings high; loops at 1). */
const beatAt = (p: number): W => ({
  az: keys(p, [[0, 0.95], [0.4, -0.72, inOut], [1, 0.95, inOut]]),
  ay: keys(p, [[0, 0.1], [0.2, -0.16], [0.42, 0.12], [0.7, 0.42], [1, 0.1]]),
  ax: keys(p, [[0, 0], [0.2, 0.16], [0.45, 0.05], [0.7, -0.22], [1, 0]]),
  hz: keys(p, [[0, 0.18], [0.2, 0.28], [0.45, -0.38, outQuad], [0.72, -0.5], [0.92, 0.1], [1, 0.18]]),
  hy: keys(p, [[0, 0], [0.38, 0.02], [0.55, 0.55], [0.75, 0.95], [0.95, 0.05], [1, 0]]),
  hx: keys(p, [[0, 0], [0.25, 0.1], [0.7, -0.15], [1, 0]]),
});
/** Held out to glide: a shallow V, the hand a touch swept and the tips curling up. */
const GLIDE: W = { az: 0.14, ay: 0.06, ax: 0.04, hz: 0.08, hy: 0.12, hx: 0 };
const mix = (a: W, b: W, k: number): W => ({
  ax: lerp(a.ax, b.ax, k), ay: lerp(a.ay, b.ay, k), az: lerp(a.az, b.az, k), hx: lerp(a.hx, b.hx, k), hy: lerp(a.hy, b.hy, k), hz: lerp(a.hz, b.hz, k),
});
/** Scales a beat's swing about the glide pose (`amp` < 1 beats shallower). */
const scaled = (w: W, amp: number): W => mix(GLIDE, w, amp);
const wingR = (b: Bones<B>, w: W): void => {
  b.armR.rot(w.ax, w.ay, w.az);
  b.handR.rot(w.hx, w.hy, w.hz);
};
const wingL = (b: Bones<B>, w: W): void => {
  b.armL.rot(w.ax, -w.ay, -w.az);
  b.handL.rot(w.hx, -w.hy, -w.hz);
};
const both = (b: Bones<B>, w: W): void => {
  wingR(b, w);
  wingL(b, w);
};
/** What a beat does to the body: it is lifted on the downstroke and sags on the recovery; the tail pumps. */
const lift = (b: Bones<B>, p: number, amp = 1): void => {
  const up = sin(TAU * (p - 0.2));
  b.root.pos(0, 0.04 * amp * up, 0);
  b.body.rot(-0.05 * amp * sin(TAU * (p - 0.05)));
  // The head stays steady in space while the body heaves (a bird's gimbal).
  b.head.rot(0.05 * amp * sin(TAU * (p - 0.05)));
  b.tail.rot(0.12 * amp * sin(TAU * (p - 0.15)));
  b.legs.rot(0.08 * amp * sin(TAU * (p - 0.3)));
};

/** A smooth bump over a loop phase centred at `c` (cycles), `w` wide: loop-safe accents (a look, a caw). */
const bump = (p: number, c: number, w: number): number => {
  let d = Math.abs(p - c);
  d = Math.min(d, 1 - d);
  return d >= w ? 0 : smooth(1 - d / w);
};

/** Hovering in place: body tipped up, talons hanging, five beats of uneven strength, head turning to look. */
const crowIdle = (): Clip<B> =>
  clip<B>('idle', 1.65, true, (b, t) => {
    const P = phase(t, 1.65);
    const p = phase(t, 0.33);
    const amp = 1 + 0.14 * wave(P, 0.1) + 0.06 * wave(P, 0.3, 2);
    both(b, scaled(beatAt(p), amp));
    lift(b, p, amp * 1.2);
    b.body.rot(-0.42 + 0.05 * wave(P, 0.2));
    b.head.rot(0.36 + 0.06 * bump(P, 0.55, 0.15), 0.45 * bump(P, 0.3, 0.2) - 0.35 * bump(P, 0.75, 0.18), 0.12 * bump(P, 0.3, 0.2));
    b.jaw.rot(0.45 * bump(P, 0.78, 0.07));
    b.tail.rot(-0.32 + 0.06 * wave(P, 0.4, 2));
    b.legs.rot(-0.7 + 0.1 * wave(P, 0.15));
    b.root.pos(0.03 * wave(P, 0.1), 0.035 * wave(P, 0.35), 0.02 * cwave(P, 0.2)).rot(0, 0.12 * wave(P, 0.05), 0.06 * wave(P, 0.25));
  });

/** Flying on: two hard beats, then a long glide on a shallow V, sinking a little and banking. */
const crowWalk = (dur = 1.15): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const P = phase(t, dur);
    const beat = 0.29;
    if (t < beat * 2) {
      const p = phase(t, beat);
      both(b, beatAt(p));
      lift(b, p, 1.1);
    } else {
      const u = (t - beat * 2) / (dur - beat * 2);
      // From the top of the last upstroke down into the glide, hold it, then raise the wings for the next beat.
      const k = envelope(u, 0, 0.3, 0.78, 1, inOut);
      both(b, mix(beatAt(0), GLIDE, k));
      // The tips flex in the air of the glide.
      b.handR.rot(0, 0, 0.04 * sin(TAU * u * 2) * k);
      b.handL.rot(0, 0, -0.04 * sin(TAU * u * 2 + 0.6) * k);
    }
    // Rises on the beats and sinks through the glide; leans into the bank, the tail steering.
    b.root.pos(0, 0.05 * cwave(P, 0.32), 0).rot(0, 0.06 * wave(P, 0.1), 0.09 * wave(P, 0.55));
    b.body.rot(0.1 + 0.04 * wave(P, 0.2));
    b.head.rot(-0.1, 0.12 * wave(P, 0.7), -0.05 * wave(P, 0.55));
    b.tail.rot(0.05, 0.1 * wave(P, 0.45), 0.08 * wave(P, 0.6));
    b.legs.rot(0.3);
  });

/** Wings flung high, it stoops on its prey beak first (the peck lands at 0.13 s), tears, then beats back up. */
const crowAttack = (): Clip<B> =>
  clip<B>('attack', 0.72, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.06, { root: [0, 0, 0, 0, 0.13, -0.08], body: [-0.55, 0, 0], head: [-0.25, 0, 0], jaw: [0.55, 0, 0], tail: [-0.35, 0, 0], legs: [-0.5, 0, 0], ...wings([0, 0.35, 1.1], [0, 0.15, 0.35]) }, outQuad],
      [0.135, { root: [0, 0, 0, 0, -0.3, 0.34], body: [0.8, 0, 0], head: [0.3, 0, 0, 0, 0, 0.05], jaw: [0, 0, 0], tail: [0.4, 0, 0], legs: [-0.9, 0, 0], ...wings([0.1, 0.65, 0.35], [0, 0.75, -0.15]) }, snap],
      [0.22, { root: [0, 0, 0, 0, -0.26, 0.3], body: [0.55, 0, 0.08], head: [-0.15, 0.25, 0], jaw: [0.35, 0, 0], tail: [0.2, 0, 0], legs: [-0.7, 0, 0], ...wings([0, -0.3, -0.35], [0, 0, 0.25]) }, outBack],
      [0.4, { root: [0, 0, 0, 0, 0.06, 0.1], body: [-0.2, 0, 0], head: [0.15, 0, 0], tail: [-0.15, 0, 0], legs: [-0.2, 0, 0], ...wings([0.1, -0.12, -0.75], [0, 0, -0.35]) }, inOut],
      [0.55, { root: [0, 0, 0, 0, 0.04, 0.04], body: [-0.1, 0, 0], ...wings([0, 0.35, 0.7], [0, 0.6, -0.3]) }, inOut],
      [0.72, {}, inOut],
    ], { head: 0.015, jaw: 0.01, tail: 0.03, legs: 0.03, handL: 0.02, handR: 0.02 });
  });

/** Struck in the air: thrown back and rolled, the wings flung up out of time, every feather standing on end. */
const crowHit = (): Clip<B> =>
  clip<B>('hit', 0.45, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.05, { root: [0, 0, 0, 0, 0.06, -0.16], body: [-0.5, 0.2, 0.42], head: [-0.55, -0.35, 0.2], jaw: [0.55, 0, 0], tail: [-0.45, 0, 0.25], legs: [-0.7, 0, 0], armR: [-0.2, -0.25, 1.05], handR: [0, 0.1, 0.55], armL: [0.2, -0.35, -0.5], handL: [0, -0.55, 0.35] }, outExpo],
      [0.17, { root: [0, 0, 0, 0, -0.03, -0.1], body: [0.18, -0.08, -0.14], head: [0.2, 0.15, -0.08], jaw: [0.2, 0, 0], tail: [0.15, 0, -0.1], legs: [-0.3, 0, 0], ...wings([0, 0.05, -0.45], [0, 0, -0.25]) }, inOut],
      [0.3, { root: [0, 0, 0, 0, 0.02, -0.03], body: [-0.05, 0, 0.04], ...wings([0, 0.25, 0.5], [0, 0.4, -0.2]) }, inOut],
      [0.45, {}, inOut],
    ], { head: 0.02, tail: 0.03, handL: 0.025, handR: 0.02 });
  }, (t) => {
    // The feathers ruffle: it puffs up and settles.
    const k = span(t, 0, 0.04, outQuad) * (1 - span(t, 0.08, 0.4, inOut));
    return [1 + 0.12 * k, 1 + 0.1 * k, 1 + 0.08 * k];
  });

/** Dazed: it tumbles low through the air on ragged, out-of-step beats, rolling and lolling, beak agape. */
const crowStun = (): Clip<B> =>
  clip<B>('stun', 1.6, true, (b, t) => {
    const P = phase(t, 1.6);
    // The right wing beats four times, the left three: they never fall in step.
    wingR(b, scaled(beatAt(phase(t, 0.4)), 0.75 + 0.2 * wave(P, 0.1)));
    wingL(b, scaled(beatAt(phase(t + 0.13, 1.6 / 3)), 0.55 + 0.25 * wave(P, 0.6)));
    b.root.pos(0.06 * wave(P, 0.3), -0.22 + 0.06 * wave(P, 0, 2), 0.04 * cwave(P)).rot(0.18 * wave(P, 0.25), 0.4 * wave(P, 0.05), 0.5 * wave(P) + 0.15 * wave(P, 0.2, 3));
    b.body.rot(0.15 + 0.12 * wave(P, 0.4, 2), 0, 0.1 * wave(P, 0.7));
    b.head.rot(0.35 + 0.2 * wave(P, 0.15, 2), 0.45 * cwave(P, 0.15), 0.4 * wave(P, 0.15));
    b.jaw.rot(0.3 + 0.12 * wave(P, 0.3, 3));
    b.tail.rot(-0.2 + 0.15 * wave(P, 0.5, 2), 0.2 * wave(P, 0.3), 0.2 * wave(P, 0.1));
    b.legs.rot(-0.4 + 0.25 * wave(P, 0.35, 2));
  });

/** Shot down: it convulses with a soundless caw, the wings give, it plummets turning and lands in a heap. */
const crowDie = (): Clip<B> =>
  clip<B>('die', 1.2, false, (b, t) => {
    const heap: P = {
      root: [0, 0, 0, 0, GROUND, 0.16], body: [0.12, 0.3, 0.95], head: [0.3, 0.55, 0.45], jaw: [0.35, 0, 0], tail: [-0.1, 0.25, -0.35], legs: [-0.5, 0, 0.3],
      armR: [0.2, 0.45, -0.42], handR: [0.1, 0.95, -0.55], armL: [0.05, -0.2, -0.95], handL: [0, -0.35, 0.05],
    };
    const bounce: P = { ...heap, root: [0, 0, 0, 0, GROUND + 0.05, 0.15], body: [0.12, 0.3, 0.8], armR: [0.15, 0.3, -0.1], handR: [0, 0.6, 0.0], armL: [0.1, -0.1, -0.8], handL: [0, -0.2, -0.1] };
    timeline(b, t, [
      [0, {}],
      [0.1, { root: [0, 0, 0, 0, 0.1, -0.06], body: [-0.65, 0, 0.2], head: [-0.65, 0, 0], jaw: [0.65, 0, 0], tail: [-0.35, 0, 0], legs: [-0.6, 0, 0], ...wings([0, 0.15, 1.15], [0, 0.1, 0.35]) }, outExpo],
      [0.35, { root: [0, 0, 0, 0, -0.18, 0.06], body: [0.35, 0.15, 0.6], head: [0.4, 0.3, 0.2], jaw: [0.4, 0, 0], tail: [0.15, 0, 0.2], legs: [0.3, 0, 0], armR: [0, 0.5, 0.75], handR: [0, 0.6, -0.2], armL: [0, -0.8, -0.2], handL: [0, -0.7, 0.3] }, inOut],
      [0.68, { ...heap, body: [0.15, 0.3, 0.75], armR: [0.1, 0.3, 0.3], handR: [0, 0.4, 0.2], armL: [0.1, -0.2, -0.6], handL: [0, -0.3, -0.1] }, inQuad],
      [0.8, bounce, outQuad],
      [0.95, heap, inQuad],
      [1.2, heap, outCubic],
    ], { head: 0.04, jaw: 0.02, tail: 0.03, handL: 0.04, handR: 0.05, armR: 0.02 });
  }, (t) => {
    // The impact flattens it for a beat.
    const k = Math.max(0, settle(t - 0.68, 3, 9)) * span(t, 0.67, 0.69);
    return [1 + 0.1 * k, 1 - 0.14 * k, 1 + 0.08 * k];
  });

export const crowClips = (): Clip[] => [crowIdle(), crowWalk(), crowAttack(), crowHit(), crowStun(), crowDie()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { crow: crowClips };
